import React, { useState } from 'react';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Settings, Building2, User, Bell, Shield, 
  Save, Mail, Phone, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const EscritorioConfiguracoes = () => {
  const { escritorio, loading } = useEmpresa();
  const { user } = useAuth();
  
  const [salvando, setSalvando] = useState(false);
  const [notificacoes, setNotificacoes] = useState({
    emailInconsistencias: true,
    emailAtestados: true,
    emailResumo: false,
    pushInconsistencias: true
  });

  const handleSalvarNotificacoes = async () => {
    setSalvando(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escritorio-configuracoes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configurações</h1>
          <p className="text-slate-600 mt-1">
            Gerencie as configurações do escritório
          </p>
        </div>
      </div>

      <Tabs defaultValue="escritorio" className="space-y-6">
        <TabsList>
          <TabsTrigger value="escritorio" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Escritório
          </TabsTrigger>
          <TabsTrigger value="usuario" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Meu Perfil
          </TabsTrigger>
          <TabsTrigger value="notificacoes" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notificações
          </TabsTrigger>
        </TabsList>

        {/* Aba Escritório */}
        <TabsContent value="escritorio">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Dados do Escritório
              </CardTitle>
              <CardDescription>
                Informações do escritório de contabilidade
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {escritorio ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Razão Social</Label>
                      <Input value={escritorio.razao_social || ''} disabled className="mt-1" />
                    </div>
                    <div>
                      <Label>Nome Fantasia</Label>
                      <Input value={escritorio.nome_fantasia || ''} disabled className="mt-1" />
                    </div>
                    <div>
                      <Label>CNPJ</Label>
                      <Input value={escritorio.cnpj || ''} disabled className="mt-1" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <div className="mt-1">
                        <Badge variant={escritorio.ativo ? 'default' : 'secondary'}>
                          {escritorio.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Endereço
                    </h4>
                    {escritorio.endereco ? (
                      <div className="text-slate-600">
                        <p>
                          {escritorio.endereco.logradouro}, {escritorio.endereco.numero}
                          {escritorio.endereco.complemento && ` - ${escritorio.endereco.complemento}`}
                        </p>
                        <p>
                          {escritorio.endereco.bairro} - {escritorio.endereco.cidade}/{escritorio.endereco.uf}
                        </p>
                        <p>CEP: {escritorio.endereco.cep}</p>
                      </div>
                    ) : (
                      <p className="text-slate-500">Endereço não cadastrado</p>
                    )}
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Limites
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Limite de Empresas</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {escritorio.limite_empresas || 10}
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">Limite de Usuários</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {escritorio.limite_usuarios || 5}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Dados do escritório não disponíveis</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Meu Perfil */}
        <TabsContent value="usuario">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Meu Perfil
              </CardTitle>
              <CardDescription>
                Suas informações de usuário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Nome</Label>
                  <Input value={user?.nome || ''} disabled className="mt-1" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled className="mt-1" />
                </div>
                <div>
                  <Label>Perfil</Label>
                  <div className="mt-1">
                    <Badge variant="outline" className="capitalize">
                      {user?.perfil?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input value={user?.telefone || '-'} disabled className="mt-1" />
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button variant="outline" disabled>
                  <Settings className="w-4 h-4 mr-2" />
                  Alterar Senha
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Para alterar sua senha, entre em contato com o administrador da plataforma.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Notificações */}
        <TabsContent value="notificacoes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-600" />
                Preferências de Notificação
              </CardTitle>
              <CardDescription>
                Configure como deseja receber notificações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Notificações por Email
                </h4>
                
                <div className="space-y-4 pl-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Inconsistências de Ponto</p>
                      <p className="text-sm text-slate-500">Receber email quando houver novas inconsistências</p>
                    </div>
                    <Switch 
                      checked={notificacoes.emailInconsistencias}
                      onCheckedChange={(checked) => setNotificacoes(prev => ({...prev, emailInconsistencias: checked}))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Atestados Pendentes</p>
                      <p className="text-sm text-slate-500">Receber email quando houver atestados para validar</p>
                    </div>
                    <Switch 
                      checked={notificacoes.emailAtestados}
                      onCheckedChange={(checked) => setNotificacoes(prev => ({...prev, emailAtestados: checked}))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Resumo Diário</p>
                      <p className="text-sm text-slate-500">Receber resumo diário de todas as empresas</p>
                    </div>
                    <Switch 
                      checked={notificacoes.emailResumo}
                      onCheckedChange={(checked) => setNotificacoes(prev => ({...prev, emailResumo: checked}))}
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button onClick={handleSalvarNotificacoes} disabled={salvando}>
                  <Save className="w-4 h-4 mr-2" />
                  {salvando ? 'Salvando...' : 'Salvar Preferências'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EscritorioConfiguracoes;
